import { useEffect, useRef, useState, type ReactNode } from "react";
import { formatNumber, useT, type Translator } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { cursorsMessages } from "@/i18n/messages/cursors";
import { cn } from "@/lib/cn";
import { AgentCursorArrow } from "./agent-cursor-arrow";
import { AgentCursorQuestionBadge } from "./agent-cursor-question-badge";
import { CursorStatusBubble } from "./cursor-status-bubble";
import type { Rect } from "@/lib/edge-aware-placement";

interface AgentCursorProps {
  agentName: string;
  agentColor: string;
  toolCall?: string;
  toolFailed?: boolean;
  content?: string;
  /** Full assistant text when the reply exceeds the snippet cap.
   *  When set and longer than `content`, an expand toggle is rendered. */
  contentFull?: string;
  thinking?: boolean;
  /** Terminal-state message shown inline in red (failed execution). */
  error?: string;
  /** Awaiting-input id of the cursor's pending question. The question renders
   *  in its work toast; the cursor shows a "?" name tag that expands it. */
  questionAwaitingInputId?: string;
  /** Fallback for a question no toast holds: open the cursor's own chat. */
  onQuestionExpandMissed?: () => void;
  /** Soft client-side stall: no `cursor.move` events for the configured
   *  threshold while the execution is still running. Surfaces a warning
   *  chip with elapsed time + a Cancel affordance so the user isn't left
   *  staring at a thinking shimmer for a hung CLI. Distinct from `error`
   *  (which is terminal). */
  stalledSinceMs?: number;
  /** Click handler for the Cancel button shown on a stalled cursor. */
  onCancel?: () => void;
  /** Click handler for the "Switch model" button on a stalled cursor —
   *  opens the agent's settings panel so the user can pick a different
   *  model when the current one is hung. */
  onSwitchModel?: () => void;
  /** Hide passive status text while preserving the question badge. Used
   *  during compact multi-monitor cursor handoff so captions do not split
   *  across display overlay windows. */
  suppressStatus?: boolean;
  /** Enables the small hover-only dismissal affordance for presented cursors
   *  and finalized compact replies. */
  onDismiss?: () => void;
  /** Lets terminal compact status bubbles move independently while the cursor
   *  arrow remains anchored to its original target. */
  statusDraggable?: boolean;
  /** Shows the colored annotation pointer. Ordinary undocked runtime status
   *  sets this false so presence is represented by the unified Denker orb. */
  pointing?: boolean;
  bubbleExclusions?: Rect[];
  statusPill?: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/** The agent's pointer and its status. Point narration and undocked passive
 *  status share the compact Denker status surface, so presence reads as one
 *  morphing surface; only the arrow carries the agent color. */
export function AgentCursor({
  agentName,
  agentColor,
  toolCall,
  toolFailed,
  content,
  contentFull,
  thinking,
  error,
  questionAwaitingInputId,
  onQuestionExpandMissed,
  stalledSinceMs,
  onCancel,
  onSwitchModel,
  suppressStatus = false,
  onDismiss,
  statusDraggable = false,
  pointing = true,
  bubbleExclusions,
  statusPill,
  className,
  "data-testid": testId = "agent-cursor",
}: AgentCursorProps) {
  const isToolCall = !!toolCall;
  const showThinking = thinking && !toolCall && !content && !error;
  const hasMore = !!(
    contentFull &&
    content &&
    contentFull.length > content.length
  );

  // Expand toggle is local to the cursor — collapsing is the default
  // and resets whenever the cursor unmounts (e.g., execution complete).
  // Reset to collapsed if the underlying content changes (next reply).
  const [expanded, setExpanded] = useState(false);
  const lastContentRef = useRef(content);
  useEffect(() => {
    if (lastContentRef.current === content) return;
    lastContentRef.current = content;
    setExpanded(false);
  }, [content]);
  const displayedText = expanded && contentFull ? contentFull : content;

  // Pick the active status. Passive runtime messages render as floating
  // captions; while a question is pending the badge is the only output.
  type Status =
    | { kind: "error"; text: string }
    | { kind: "tool"; text: string; failed?: boolean }
    | { kind: "reply"; text: string }
    | { kind: "thinking" }
    | null;
  const status: Status = error
    ? { kind: "error", text: error }
    : isToolCall
      ? { kind: "tool", text: toolCall!, failed: toolFailed }
      : displayedText
        ? { kind: "reply", text: displayedText }
        : showThinking
          ? { kind: "thinking" }
          : null;

  return (
    <div
      className={cn("pointer-events-none relative", className)}
      data-testid={testId}
    >
      {pointing ? (
        <AgentCursorArrow
          color={agentColor}
          mode="glass"
          shape="soft"
          liquidLevel={0.26}
        />
      ) : null}

      {/* Point narration anchors beside the arrow. Ordinary undocked status
       * starts at the origin because its orb/card is the complete presence. */}
      <div
        className={cn(
          "absolute flex flex-col items-start gap-1 leading-none",
          pointing ? "left-3 top-3" : "left-0 top-0",
        )}
      >
        {/* A host-owned status pill may carry the question; otherwise it
         *  renders in its work toast or frame and the badge stands in. */}
        {statusPill !== undefined
          ? statusPill
          : status &&
            !questionAwaitingInputId &&
            !suppressStatus && (
              <CursorStatusBubble
                agentName={agentName}
                status={status}
                expanded={expanded}
                hasMore={hasMore}
                onToggleExpand={() => setExpanded((v) => !v)}
                onDismiss={onDismiss}
                draggable={statusDraggable}
                exclusions={bubbleExclusions}
                testId={testId}
              />
            )}
        {questionAwaitingInputId && (
          <AgentCursorQuestionBadge
            agentName={agentName}
            awaitingInputId={questionAwaitingInputId}
            testId={testId}
            onExpandMissed={onQuestionExpandMissed}
          />
        )}
        {/* Stall warning — only when the cursor is otherwise idle (no
         *  toolCall / no content / no question / no error) and the gap
         *  since lastSeen has crossed the threshold. Surfaces an inline
         *  "no reply yet" with elapsed time + a Cancel button. */}
        {stalledSinceMs !== undefined &&
          !error &&
          !questionAwaitingInputId &&
          !suppressStatus &&
          !toolCall &&
          !content && (
            <CursorStallChip
              stalledSinceMs={stalledSinceMs}
              onCancel={onCancel}
              onSwitchModel={onSwitchModel}
              testId={testId}
            />
          )}
      </div>
    </div>
  );
}

/* ---------- CursorStallChip ---------- */

function formatStallDuration(
  ms: number,
  t: Translator<typeof cursorsMessages.en>,
): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60)
    return t("durationSeconds", { seconds: formatNumber(seconds) });
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds - minutes * 60;
  return remainder === 0
    ? t("durationMinutes", { minutes: formatNumber(minutes) })
    : t("durationMinutesSeconds", {
        minutes: formatNumber(minutes),
        seconds: formatNumber(remainder),
      });
}

function CursorStallChip({
  stalledSinceMs,
  onCancel,
  onSwitchModel,
  testId,
}: {
  stalledSinceMs: number;
  onCancel?: () => void;
  onSwitchModel?: () => void;
  testId: string;
}) {
  const t = useT(cursorsMessages);
  const common = useT(commonMessages);
  return (
    <div
      className={cn(
        "pointer-events-auto flex w-max max-w-[360px] shrink-0 flex-wrap items-center gap-1",
        "rounded-menu border bg-glass-fill-dense px-2.5 py-1.5",
        "text-appkit-small leading-relaxed text-warning shadow-glass-sm backdrop-blur-glass",
      )}
      data-testid={`${testId}-stalled`}
    >
      <span className="mr-1">
        {t("stalledNoReply", {
          duration: formatStallDuration(stalledSinceMs, t),
        })}
      </span>
      {onSwitchModel && (
        <button
          type="button"
          onClick={onSwitchModel}
          className="rounded-control-sm px-1.5 py-0.5 text-appkit-small font-medium text-primary hover:bg-glass-fill-heavy"
          data-testid={`${testId}-stalled-switch-model`}
        >
          {t("switchModel")}
        </button>
      )}
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="rounded-control-sm px-1.5 py-0.5 text-appkit-small font-medium text-primary hover:bg-glass-fill-heavy"
          data-testid={`${testId}-stalled-cancel`}
        >
          {common("cancel")}
        </button>
      )}
    </div>
  );
}

export type { AgentCursorProps };
